// @flow

import React, {PureComponent} from 'react';
import styled from 'styled-components';
import {Link} from '@reach/router';
import qs from 'query-string';
import {connect} from 'react-redux';

import notification from 'antd/lib/notification';
import Button from 'antd/lib/button';
import Popconfirm from 'antd/lib/popconfirm';

import {type FetchListParams, osagoCalculationApi, osagoCalculationYearApi} from './../../../lib/api';
import type {ListState, OsagoCalculation, UserAccess, VehicleModel} from '../../../lib/types';
import {
  applyMaskToValue,
  getListInitialState,
  navigate,
  setQueryParams,
  sumWidthColumns,
  toLocalStringRu,
} from './../../../lib/helpers';
import type {OsagoCalculationStatus, OsagoCalculationYear} from '../../../lib/types/osagoCalculation';
import {osagoCalculationStatusEnum, vehicleGroups, vehicleTypes} from './../../../lib/enum';
import {accessTypeEnum} from '../../../lib/enum/userAccess';

import {Icon, Table} from './../../../components/ui';
import {TableHeader} from './../../../components/ui/Table';
import {Section} from './../../../components/layout';
import Header from './../../../components/layout/Header';
import {notificationLoading} from '../../../components/Notifications';
import {formatLicensePlateMask} from '../../../components/masked-inputs/LicensePlateInput';
import type {AppState} from '../../../ducks/redux';

import Filter, {type OsagoCalculationFilterParams} from './Filter';

const StyledIcon = styled(Icon)`
  margin: 0 5px;
  color: #1890ff;
  cursor: pointer;
`;
const Operations = styled.div`
  & * {
    margin-right: 16px;
    &:last-child {
      margin-right: 0;
    }
  }
`;

type Props = {
  location: Location & { state: { page: number } },
  userAccess: UserAccess[]
};

type State = ListState<OsagoCalculation> & {
  filter: OsagoCalculationFilterParams,
  osagoCalculationYear: ?OsagoCalculationYear
};

export class OsagoCalculationsList extends PureComponent<Props, State> {
  state = {
    ...getListInitialState(),
    osagoCalculationYear: null,
    filter: {}
  };

  async deleteOsagoCalculation(id: number) {
    const { page } = this.state;
    try {
      notificationLoading({
        message: 'Удаление...',
        key: 'deleting'
      });
      await osagoCalculationApi.deleteOsagoCalculation(id);
      notification.success({
        message: 'Успешное удаление',
        description: `Запись расчета успешна удалена`
      });
      await this.fetchOsagoCalculations(page);
    } catch (error) {
      notification.error({
        message: 'Ошибка при удалении',
        description: error.message
      });
    } finally {
      notification.close('deleting');
    }
  }

  columns = [
    {
      title: 'Тип/Модель',
      dataIndex: 'vehicleModel',
      fixed: 'left',
      width: 300,
      breakByWidth: true,
      sorter: true,
      render: (vehicleModel: VehicleModel, record: OsagoCalculation) => {
        if (record.vehicle) {
          return [
            record.vehicle.vehicleModel.brandName,
            record.vehicle.vehicleModel.name
          ]
            .join(' ')
            .trim();
        }
        if (vehicleModel) {
          return [vehicleModel.brandName, vehicleModel.name].join(' ').trim();
        }
        return record.vehicleType
          ? vehicleTypes[record.vehicleType]
          : vehicleGroups[record.vehicleGroup];
      }
    },
    {
      title: 'Гос. номер',
      dataIndex: 'vehicle.licensePlate',
      fixed: 'left',
      width: 150,
      breakByWidth: true,
      sorter: true,
      render: (licensePlate: ?string, record: any) =>
        licensePlate && (
          <Link to={`/vehicles/${record.vehicle.id}`}>
            {applyMaskToValue(licensePlate, formatLicensePlateMask)}
          </Link>
        )
    },
    {
      title: 'Год выпуска',
      dataIndex: 'vehicle.yearIssued',
      width: 100,
      breakByWidth: true,
      render: (yearIssued: number, record: OsagoCalculation) => {
        if (yearIssued) {
          return yearIssued;
        }
        return record.yearIssued;
      }
    },
    {
      title: 'Расчетный год',
      dataIndex: 'year',
      width: 100,
      breakByWidth: true
    },
    {
      title: <TableHeader>Базовая ставка (тек.)</TableHeader>,
      dataIndex: 'currentBaseRate',
      width: 100,
      breakByWidth: true,
      sorter: true,
      render: toLocalStringRu
    },
    {
      title: <TableHeader>Базовая ставка (мин)</TableHeader>,
      dataIndex: 'minBaseRate',
      sorter: true,
      width: 100,
      breakByWidth: true,
      render: toLocalStringRu
    },
    {
      title: <TableHeader>Базовая ставка (макс)</TableHeader>,
      dataIndex: 'maxBaseRate',
      sorter: true,
      width: 100,
      breakByWidth: true,
      render: toLocalStringRu
    },
    {
      title: 'КТ',
      dataIndex: 'kt',
      sorter: true,
      width: 100,
      breakByWidth: true,
      render: toLocalStringRu
    },
    {
      title: 'КБМ',
      dataIndex: 'kbm',
      sorter: true,
      width: 100,
      breakByWidth: true,
      render: toLocalStringRu
    },
    {
      title: 'КО',
      dataIndex: 'ko',
      sorter: true,
      width: 100,
      breakByWidth: true,
      render: toLocalStringRu
    },
    {
      title: 'КВС',
      dataIndex: 'kvs',
      sorter: true,
      width: 100,
      breakByWidth: true,
      render: toLocalStringRu
    },
    {
      title: 'КС',
      dataIndex: 'kc',
      sorter: true,
      width: 100,
      breakByWidth: true,
      render: toLocalStringRu
    },
    {
      title: 'КП',
      dataIndex: 'kp',
      sorter: true,
      width: 100,
      breakByWidth: true,
      render: toLocalStringRu
    },
    {
      title: 'КМ',
      dataIndex: 'km',
      sorter: true,
      width: 100,
      breakByWidth: true,
      render: toLocalStringRu
    },
    {
      title: 'КПР',
      dataIndex: 'kpr',
      sorter: true,
      width: 100,
      breakByWidth: true,
      render: toLocalStringRu
    },
    {
      title: <TableHeader>Сумма по текущему значению</TableHeader>,
      dataIndex: 'curInsuranceSum',
      sorter: true,
      width: 100,
      breakByWidth: true,
      render: toLocalStringRu
    },
    {
      title: <TableHeader>Сумма по минимальной ставке</TableHeader>,
      dataIndex: 'minInsuranceSum',
      sorter: true,
      width: 100,
      breakByWidth: true,
      render: toLocalStringRu
    },
    {
      title: <TableHeader>Сумма по максимальной ставке</TableHeader>,
      dataIndex: 'maxInsuranceSum',
      sorter: true,
      width: 100,
      breakByWidth: true,
      render: toLocalStringRu
    }
  ];

  fetchOsagoCalculations = async (
    page: number = 1,
    params: FetchListParams<OsagoCalculationFilterParams> = {}
  ) => {
    try {
      const { filter } = this.state;
      this.setState({ loading: true });

      const {
        data,
        totalCount
      } = await osagoCalculationApi.fetchOsagoCalculations({
        ...filter,
        page,
        ...params
      });
      setQueryParams({ page });
      this.setState({ data, totalCount, page });
    } catch (err) {
      notification.error({
        message: 'Ошибка',
        description: err && err.message
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  cleanFilter = () => {
    const { osagoCalculationYearId } = this.state.filter; // год не сбрасываем
    parseInt(osagoCalculationYearId) > 0 &&
      setQueryParams({ osagoCalculationYearId });
    this.setState(
      { filter: { osagoCalculationYearId } },
      this.fetchOsagoCalculations
    );
  };

  applyFilter = (filter: OsagoCalculationFilterParams) =>
    this.setState({ filter }, this.fetchOsagoCalculations);

  async componentDidMount() {
    const { page, ...filter } = qs.parse(window.location.search);
    const { osagoCalculationYearId } = qs.parse(this.props.location.search);
    const osagoCalculationYear = await osagoCalculationYearApi.getOsagoCalculationYear(
      osagoCalculationYearId
    );
    this.setState({ filter, osagoCalculationYear }, () =>
      this.fetchOsagoCalculations(page)
    );
    if (this.canHandle()) {
      this.columns.push({
        title: '',
        width: 40,
        render: (record: OsagoCalculation) => (
          <Operations>
            <Popconfirm
              title="Вы действительно хотите удалить?"
              okText="Да"
              cancelText="Нет"
              onConfirm={() =>
                this.deleteOsagoCalculation(parseInt(record.id, 10))
              }
            >
              <StyledIcon type="x" />
            </Popconfirm>
          </Operations>
        )
      });
    }
  }

  handleRowClick = (id: number) => navigate(`/budget/osago-calculations/${id}`);

  handlePrint = async () => {
    try {
      notificationLoading({
        message: 'Формирование файла для печати',
        key: 'print'
      });
      const { osagoCalculationYearId } = qs.parse(this.props.location.search);
      await osagoCalculationApi.print(osagoCalculationYearId);
    } catch (error) {
      notification.error({
        message: 'Ошибка',
        description: error.message
      });
    } finally {
      notification.close('print');
    }
  };

  canHandle = () => {
    return this.props.userAccess.some(access =>
      [
        accessTypeEnum.admin,
        accessTypeEnum.adminBranch,
        accessTypeEnum.formationBranchOsagoCalculations,
        accessTypeEnum.formationAllBranchesOsagoCalculations
      ].includes(access)
    );
  };

  canApprove = () => {
    return this.props.userAccess.some(access =>
      [
        accessTypeEnum.admin,
        accessTypeEnum.adminBranch,
        accessTypeEnum.approvingAllBranchesOsagoCalculations,
        accessTypeEnum.approvingBranchOsagoCalculations
      ].includes(access)
    );
  };

  /**
   * Смена статуса года
   * @param status Статус
   */
  changeStatus = async (status: OsagoCalculationStatus) => {
    // $FlowFixMe flow говоритБ что нет параметра osagoCalculationYear
    const { osagoCalculationYear } = this.state;
    const changed = await osagoCalculationYearApi.changeOsagoCalculationStatusYear(
      {
        ...osagoCalculationYear,
        status
      }
    );
    this.setState({ osagoCalculationYear: changed });
  };

  /**
   * Утверждение расчета потребностей ОСАГО
   */
  handleApprove = async () => {
    try {
      await this.changeStatus(osagoCalculationStatusEnum.approved);
      const { year } = this.state.osagoCalculationYear || {};
      notification.success({
        message: `Расчет потребностей ОСАГО за ${year} год был утвержден`
      });
    } catch (error) {
      notification.error({
        message: 'Ошибка',
        description: error.message
      });
    }
  };

  /**
   * Отправление на утверждение расчета потребностей ОСАГО
   */
  handleSendOnApprove = async () => {
    try {
      await this.changeStatus(osagoCalculationStatusEnum.onApproving);
      const { year } = this.state.osagoCalculationYear || {};
      notification.success({
        message: `Расчет потребностей ОСАГО за ${year} год был отправлен на утверждение`
      });
    } catch (error) {
      notification.error({
        message: 'Ошибка',
        description: error.message
      });
    }
  };

  render() {
    const { location } = this.props;
    const {
      data,
      totalCount,
      page,
      pageSize,
      loading,
      filter,
      osagoCalculationYear
    } = this.state;

    const canHandle = this.canHandle();
    const canApprove = this.canApprove();
    const { status, year } = osagoCalculationYear || {};

    return (
      <>
        <Header
          left={<h1>Расчет потребностей ОСАГО за {year} год</h1>}
          right={
            <Operations>
              {canHandle && status === osagoCalculationStatusEnum.draft && (
                <Button onClick={this.handleSendOnApprove} type="primary">
                  На утверждение
                </Button>
              )}
              {canApprove && status === osagoCalculationStatusEnum.onApproving && (
                <Button onClick={this.handleApprove} type="primary">
                  Утвердить
                </Button>
              )}
              <Button onClick={this.handlePrint}>Печать</Button>
            </Operations>
          }
        />
        <Section>
          <Filter
            filter={filter}
            applyFilter={this.applyFilter}
            cleanFilter={this.cleanFilter}
          />
          <Table
            columns={this.columns}
            data={data.map(model => ({
              ...model,
              key: model.id
            }))}
            bordered
            fetch={this.fetchOsagoCalculations}
            loading={loading}
            scroll={{
              y: 'calc(100vh - 270px)',
              x: sumWidthColumns(this.columns) + 300
            }}
            pagination={{
              page,
              pageSize,
              totalCount,
              location
            }}
          />
        </Section>
      </>
    );
  }
}

export default connect((state: AppState) => ({
  userAccess: state.auth.profile.access
}))(OsagoCalculationsList);
